import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { isLinePending, useNetworkPricesStore } from '../NetworkPrices.store';
import { editClusterPrice } from '../services';
import { LineKey, NetworkPriceCluster, NetworkPriceLine, NetworkPricesPage, ProductsToBePricedId } from '../types';
import { NETWORK_PRICES_QUERY_FILTER, reloadNetworkLine, startLineReload } from '../networkLineReload';
import { calcNetworkAveragePrice, confirmClusterPrice, getLineKey, isSamePrice } from '../utils';

type EditClusterPriceVariables = {
  line: NetworkPriceLine;
  cluster: NetworkPriceCluster;
  cellId: ProductsToBePricedId;
  price: number;
};

type EditClusterPriceActions = {
  commitClusterPrice: (line: NetworkPriceLine, cluster: NetworkPriceCluster) => void;
};

const applyConfirmedPrice = (page: NetworkPricesPage, lineKey: LineKey, cellId: ProductsToBePricedId, price: number): NetworkPricesPage => ({
  ...page,
  content: page.content.map(line => {
    if (getLineKey(line) !== lineKey) return line;
    const clusters = line.clusters.map(cluster => (cluster.productsToBePricedId === cellId ? confirmClusterPrice(cluster, price) : cluster));
    const confirmedLine = { ...line, clusters };
    return { ...confirmedLine, averagePrice: calcNetworkAveragePrice(confirmedLine, {}) };
  }),
});

export const useEditClusterPrice = (): EditClusterPriceActions => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: ({ cellId, price }: EditClusterPriceVariables) => editClusterPrice(cellId, price),
    onSuccess: async (_data, { line, cellId, price }) => {
      const lineKey = getLineKey(line);
      const lineReload = startLineReload(queryClient, lineKey);

      await queryClient.cancelQueries(NETWORK_PRICES_QUERY_FILTER);
      queryClient.setQueriesData<NetworkPricesPage>(NETWORK_PRICES_QUERY_FILTER, cached => cached && applyConfirmedPrice(cached, lineKey, cellId, price));
      await reloadNetworkLine(queryClient, line, lineReload);

      useNetworkPricesStore.getState().clearDraftPrice(cellId);
      lineReload.finish();
    },
    onError: (error: unknown, { line, cluster }) => {
      AlertWithTimestamp({
        message: extractApiErrorMessage(error) ?? `Erro ao salvar alteração no produto ${line.productId} - ${cluster.clusterName}`,
      });
    },
    onSettled: (_data, _error, { cellId }) => {
      useNetworkPricesStore.getState().finishPendingCell(cellId);
    },
  });

  const commitClusterPrice = useCallback(
    (line: NetworkPriceLine, cluster: NetworkPriceCluster) => {
      const cellId = cluster.productsToBePricedId;
      if (cellId === null) return;
      const state = useNetworkPricesStore.getState();
      // The blur of an editor removed by a resize remount is not the user leaving the field.
      if (state.isTableRemounting || isLinePending(state, getLineKey(line), [cellId])) return;
      const draft = state.draftPrices[cellId];

      // Compared with the server price, not the focus value: a draft left by a failed save stays and is retried.
      if (draft === undefined || draft === null || !Number.isFinite(draft) || (cluster.price !== null && isSamePrice(draft, cluster.price))) {
        state.clearDraftPrice(cellId);
        return;
      }

      state.startPendingCell(cellId);
      mutate({ line, cluster, cellId, price: draft });
    },
    [mutate],
  );

  return { commitClusterPrice };
};
